import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import ChassisDropdown from '../../shared/components/ChassisDropdown.jsx';
import { useGetChassisListQuery } from '../../../../../api/endpoints/addperiod';
import { InputTextarea } from 'primereact/inputtextarea';
import GenericFileUpload from '../../../../../components/common/GenericFileUpload';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { selectUserRoles } from '../../../../../features/auth/authSlice';
import { Roles } from '../../../../../utils/roleEnums';
import { VAFStatus } from '../constants/vafStatus.js';
import DateChangeRequestField from '../../shared/components/DateChangeRequestField.jsx';
import { FORM_TYPE, DATE_FIELD } from '../../../../../utils/formDateChangeConstants';

const VehicleAcceptanceFormDialog = ({
    visible,
    onHide,
    formData,
    recordId,
    onInputChange,
    onNumberChange,
    onDateChange,
    onDropdownChange,
    onSubmit,
    formSubmitted,
    isSubmitting,
    isEditMode,
    updatedFiles,
    onEditFilesChange,
    onRemoveFile,
    onDelete,
    isDeleting,
    onSendForApproval,
    isSendingForApproval,
    onReturn,
    isReturning,
    onApprove,
    isApproving,
    documents,
    isLoadingDocuments,
    onDeleteDocument,
    onDownloadDocument,
    onUpdateDocument,
    toastRef,
    hasDocumentOperations,
    isDownloadingDocument,
    isDeletingDocument,
    isUpdatingDocument,
    hasFormDataChanged,
    isWaitingForDocumentConfirmation,
    th
}) => {
    const toast = useRef(null);
    const [message, setMessage] = useState('');
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

    // Authorization for Return/Approve buttons
    const userRoles = useSelector(selectUserRoles);
    const authorizationCheckforVehicleAcceptance =
        Array.isArray(userRoles) &&
        (userRoles.includes(Roles.WARRANTY_TEAM) ||
            userRoles.includes(Roles.WARRANTY_MANAGER) ||
            userRoles.includes(Roles.SSH_MANAGER) ||
            userRoles.includes(Roles.SSH_DIRECTOR) ||
            userRoles.includes(Roles.SSH_GENERAL_MANAGER) ||
            userRoles.includes(Roles.CEO) ||
            userRoles.includes(Roles.ADMIN));

    const dialogHeader = isEditMode ? th('warranty.vehicleAcceptanceForm.dialog.editTitle') : th('warranty.vehicleAcceptanceForm.dialog.createTitle');

    // Dosya işlemleri durumu - sadece indirme, silme ve güncelleme işlemleri sırasında
    const isProcessingFiles = isDownloadingDocument || isDeletingDocument || isUpdatingDocument;

    // Form pasif olması gereken durumlar - dosya işlemi veya onay bekleme
    const isFormDisabled = isProcessingFiles || isWaitingForDocumentConfirmation;

    const processingMessage = isDownloadingDocument
        ? th('warranty.vehicleAcceptanceForm.messages.processing')
        : isDeletingDocument
        ? th('warranty.vehicleAcceptanceForm.messages.processing')
        : isUpdatingDocument
        ? th('warranty.vehicleAcceptanceForm.messages.processing')
        : isWaitingForDocumentConfirmation
        ? th('warranty.vehicleAcceptanceForm.messages.processing')
        : '';

    useEffect(() => {
        if (visible) {
            setMessage('');
        }
    }, [visible]);

    // Dialog'daki toast ref'ini dışarıya aktar
    useEffect(() => {
        if (toastRef && toast.current) {
            toastRef.current = toast.current;
        }
    }, [toastRef, toast.current]);

    // Dialog kapatma işlemi - değişiklik kontrolü
    const handleDialogHide = () => {
        // Dosya işlemi veya onay bekleniyorsa kapatmayı engelle
        if (isFormDisabled) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: isWaitingForDocumentConfirmation ? th('warranty.vehicleAcceptanceForm.messages.processing') : th('warranty.vehicleAcceptanceForm.messages.processing'),
                life: 3000
            });
            return;
        }

        // Form değişiklik kontrolü - sadece edit modunda ve form değişmişse
        if (isEditMode && typeof hasFormDataChanged === 'function' && hasFormDataChanged()) {
            setShowUnsavedChangesDialog(true);
        } else {
            onHide();
        }
    };

    // Unsaved changes dialog'unu onayla
    const handleConfirmExit = () => {
        setShowUnsavedChangesDialog(false);
        onHide();
    };

    // Unsaved changes dialog'unu iptal et
    const handleCancelExit = () => {
        setShowUnsavedChangesDialog(false);
    };

    const isReadOnly = isEditMode && formData?.status !== VAFStatus.DRAFT && formData?.status !== VAFStatus.RETURNED;

    const handleApprovalAction = (action, requiresMessage = true) => {
        if (requiresMessage && !message) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('warranty.vehicleAcceptanceForm.messages.approvalRequired'), life: 3000 });
            return;
        }
        action(message);
    };

    const shouldShowSendForApprovalButton = () => {
        // DRAFT ve RETURNED statusları için onaya gönder butonunu göster
        if (formData?.status !== VAFStatus.DRAFT && formData?.status !== VAFStatus.RETURNED) return false;

        // Yeni dosyalar varsa veya mevcut dosyalar varsa göster
        const hasNewFiles = updatedFiles && updatedFiles.length > 0;
        const hasExistingFiles = documents && documents.length > 0;

        return hasNewFiles || hasExistingFiles;
    };

    let footerButtons;
    if (isEditMode && formData && typeof formData.status === 'number') {
        switch (formData.status) {
            case VAFStatus.DRAFT:
            case VAFStatus.RETURNED:
                footerButtons = (
                    <>
                        {formData.status === VAFStatus.DRAFT && (
                            <Button label={th('warranty.vehicleAcceptanceForm.dialog.delete')} icon="pi pi-trash" onClick={onDelete} loading={isDeleting} className="p-button-danger" disabled={isSubmitting || isSendingForApproval || isFormDisabled} />
                        )}
                        <Button
                            label={th('warranty.vehicleAcceptanceForm.dialog.saveChanges')}
                            icon="pi pi-check"
                            onClick={onSubmit}
                            loading={isSubmitting}
                            style={{ marginLeft: '0.5em' }}
                            disabled={isDeleting || isSendingForApproval || isFormDisabled}
                        />
                        {shouldShowSendForApprovalButton() && (
                            <Button
                                label={th('warranty.vehicleAcceptanceForm.dialog.sendForApproval')}
                                icon="pi pi-send"
                                onClick={onSendForApproval}
                                loading={isSendingForApproval}
                                style={{ marginLeft: '0.5em' }}
                                disabled={isDeleting || isSubmitting || isFormDisabled}
                            />
                        )}
                    </>
                );
                break;
            case VAFStatus.SENT_FOR_APPROVAL:
                footerButtons = (
                    <>
                        {authorizationCheckforVehicleAcceptance && (
                            <Button
                                label={th('warranty.vehicleAcceptanceForm.dialog.return')}
                                icon="pi pi-arrow-left"
                                onClick={() => handleApprovalAction(onReturn, true)}
                                loading={isReturning}
                                className="p-button-warning"
                                disabled={isApproving || isFormDisabled}
                            />
                        )}
                        {authorizationCheckforVehicleAcceptance && (
                            <Button
                                label={th('warranty.vehicleAcceptanceForm.dialog.approve')}
                                icon="pi pi-check-circle"
                                onClick={() => handleApprovalAction(onApprove, false)}
                                loading={isApproving}
                                className="p-button-success"
                                style={{ marginLeft: '0.5em' }}
                                disabled={isReturning || isFormDisabled}
                            />
                        )}
                    </>
                );
                break;
            case VAFStatus.APPROVED:
                footerButtons = <Button label={th('warranty.vehicleAcceptanceForm.dialog.close')} icon="pi pi-times" onClick={handleDialogHide} className="p-button-text" disabled={isFormDisabled} />;
                break;
        }
    } else {
        footerButtons = (
            <>
                <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleDialogHide} className="p-button-text" disabled={isSubmitting || isFormDisabled} />
                <Button label={th('common.save')} icon="pi pi-check" onClick={onSubmit} loading={isSubmitting} disabled={isFormDisabled} />
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />

            {/* Main Dialog */}
            <Dialog
                visible={visible}
                style={{ width: '70vw', maxWidth: '900px' }}
                header={dialogHeader}
                modal
                className="p-fluid"
                footer={footerButtons}
                onHide={handleDialogHide}
                closable={!isFormDisabled}
                closeOnEscape={!isFormDisabled}
                dismissableMask={false}
            >
                {/* Dosya işlem durumu göstergesi */}
                {isFormDisabled && (
                    <div
                        className="flex align-items-center justify-content-center mb-3"
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            padding: '1rem',
                            borderRadius: '6px',
                            position: 'relative'
                        }}
                    >
                        <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                        <span className="ml-3 font-medium">{processingMessage}</span>
                    </div>
                )}

                <div className={`grid ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="col-12 md:col-6">
                        <div className="p-field mb-3">
                            <label htmlFor="chassisNo" className="font-bold block mb-2">
                                {th('warranty.vehicleAcceptanceForm.form.chassisNo')}
                            </label>
                            <ChassisDropdown
                                mode="remote"
                                useChassisQuery={useGetChassisListQuery}
                                placeholderKey="warranty.vehicleAcceptanceForm.form.chassisNoPlaceholder"
                                value={formData.chassisNo}
                                onChange={(value) => onDropdownChange(value, 'chassisNo')}
                                className={formSubmitted && !formData.chassisNo ? 'p-invalid' : ''}
                                disabled={isReadOnly || isFormDisabled}
                            />
                            {formSubmitted && !formData.chassisNo && <small className="p-error">{th('warranty.vehicleAcceptanceForm.validation.chassisNoRequired')}</small>}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="p-field mb-3">
                            <label htmlFor="vehicleAcceptDate" className="font-bold block mb-2">
                                {th('warranty.vehicleAcceptanceForm.form.vehicleAcceptDate')}
                            </label>
                            {isEditMode && formData?.status === VAFStatus.APPROVED ? (
                                <DateChangeRequestField
                                    formType={FORM_TYPE.VAF}
                                    dateField={DATE_FIELD.VEHICLE_ACCEPT_DATE}
                                    formId={recordId}
                                    currentDate={formData.vehicleAcceptDate}
                                    label={th('warranty.vehicleAcceptanceForm.form.vehicleAcceptDate')}
                                    disabled={isFormDisabled}
                                    onSuccess={onHide}
                                />
                            ) : (
                                <>
                                    <Calendar
                                        id="vehicleAcceptDate"
                                        value={formData.vehicleAcceptDate}
                                        onChange={(e) => onDateChange(e, 'vehicleAcceptDate')}
                                        showIcon
                                        dateFormat="dd/mm/yy"
                                        maxDate={new Date()}
                                        className={formSubmitted && !formData.vehicleAcceptDate ? 'p-invalid' : ''}
                                        disabled={isReadOnly || isFormDisabled}
                                        placeholder={th('warranty.vehicleAcceptanceForm.form.vehicleAcceptDatePlaceholder')}
                                    />
                                    {formSubmitted && !formData.vehicleAcceptDate && <small className="p-error">{th('warranty.vehicleAcceptanceForm.validation.vehicleAcceptDateRequired')}</small>}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="p-field mb-3">
                            <label htmlFor="vehicleAcceptKm" className="font-bold block mb-2">
                                {th('warranty.vehicleAcceptanceForm.form.vehicleAcceptKm')}
                            </label>
                            <InputNumber
                                id="vehicleAcceptKm"
                                value={formData.vehicleAcceptKm}
                                onValueChange={(e) => onNumberChange(e, 'vehicleAcceptKm')}
                                mode="decimal"
                                className={formSubmitted && (formData.vehicleAcceptKm === null || formData.vehicleAcceptKm === undefined) ? 'p-invalid' : ''}
                                disabled={isReadOnly || isFormDisabled}
                                placeholder={th('warranty.vehicleAcceptanceForm.form.vehicleAcceptKmPlaceholder')}
                            />
                            {formSubmitted && (formData.vehicleAcceptKm === null || formData.vehicleAcceptKm === undefined) && <small className="p-error">{th('warranty.vehicleAcceptanceForm.validation.vehicleAcceptKmRequired')}</small>}
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="p-field mb-3">
                            <label htmlFor="description" className="font-bold block mb-2">
                                {th('warranty.vehicleAcceptanceForm.form.description')}
                            </label>
                            <InputText
                                id="description"
                                value={formData.description || ''}
                                onChange={(e) => onInputChange(e, 'description')}
                                disabled={isReadOnly || isFormDisabled}
                                placeholder={th('warranty.vehicleAcceptanceForm.form.descriptionPlaceholder')}
                            />
                        </div>
                    </div>
                </div>

                {/* Dosya işlemleri bilgilendirme mesajı */}
                {isEditMode && hasDocumentOperations && (
                    <div className="mb-3">
                        <Message severity="info" text={th('warranty.vehicleAcceptanceForm.messages.fileChangesAutoSaved')} />
                    </div>
                )}

                {/* File Management and Approval Message Area */}
                {isEditMode && (
                    <div className="mt-3">
                        {/* File Upload: Always shown in edit mode */}
                        <GenericFileUpload
                            newFiles={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED ? updatedFiles || [] : []}
                            onFilesChange={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED ? onEditFilesChange : () => {}}
                            existingDocuments={documents || []}
                            onDownloadDocument={onDownloadDocument}
                            onDeleteExistingDocument={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED ? onDeleteDocument : null}
                            onUpdateExistingDocument={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED ? onUpdateDocument : null}
                            isLoading={isLoadingDocuments}
                            disabled={(formData.status !== VAFStatus.DRAFT && formData.status !== VAFStatus.RETURNED) || isFormDisabled}
                            multiple={true}
                            maxFileSize={20971520}
                            acceptedFileTypes={{
                                'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                                'application/pdf': ['.pdf'],
                                'application/msword': ['.doc'],
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                'application/vnd.ms-excel': ['.xls'],
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                'text/plain': ['.txt']
                            }}
                            showDownloadButton={true}
                            showUpdateButton={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED}
                            showDeleteButton={formData.status === VAFStatus.DRAFT || formData.status === VAFStatus.RETURNED}
                            readOnlyMode={formData.status !== VAFStatus.DRAFT && formData.status !== VAFStatus.RETURNED}
                        />

                        {/* Approval/Rejection Message: Only shown in SENT_FOR_APPROVAL status */}
                        {formData.status === VAFStatus.SENT_FOR_APPROVAL && (
                            <div className="mt-3">
                                <label htmlFor="approvalMessage" className={`block mb-2 font-bold ${!message && formSubmitted && isReturning ? 'p-error' : ''}`}>
                                    {th('warranty.vehicleAcceptanceForm.form.returnReasonLabel')}*
                                </label>
                                <InputTextarea
                                    id="approvalMessage"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={3}
                                    className={`w-full ${!message && formSubmitted && isReturning ? 'p-invalid' : ''}`}
                                    placeholder={th('warranty.vehicleAcceptanceForm.form.returnReasonPlaceholder')}
                                    disabled={isFormDisabled || !authorizationCheckforVehicleAcceptance}
                                />
                                {!message && formSubmitted && isReturning && <small className="p-error">{th('warranty.vehicleAcceptanceForm.validation.returnReasonRequired')}</small>}
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Unsaved Changes Confirmation Dialog */}
            <Dialog
                visible={showUnsavedChangesDialog}
                style={{ width: '450px' }}
                header={th('warranty.vehicleAcceptanceForm.messages.unsavedChangesHeader')}
                modal
                onHide={handleCancelExit}
                footer={
                    <div>
                        <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleCancelExit} className="p-button-text" />
                        <Button label={th('warranty.vehicleAcceptanceForm.messages.exitConfirm')} icon="pi pi-check" onClick={handleConfirmExit} className="p-button-danger" style={{ marginLeft: '0.5em' }} />
                    </div>
                }
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: 'var(--orange-500)' }} />
                    <span>{th('warranty.vehicleAcceptanceForm.messages.unsavedChangesMessage')}</span>
                </div>
            </Dialog>
        </>
    );
};

export default VehicleAcceptanceFormDialog;
